import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LoginModal({ onSuccess, onClose, onShowSignup }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/login`, { 
        email,
        password
      });
      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to login. Check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/forgot-password`, { 
        email: forgotEmail,
        new_password: newPassword
      });
      setResetSuccess(response.data.message || 'Password reset successfully! You can now login.');
      // Clear form
      setForgotEmail('');
      setNewPassword('');
      setConfirmPassword('');
      // Switch back to login after 2 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. Please check your email.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Form
  if (showForgotPassword) {
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
        <div 
          className="max-w-md w-full rounded-2xl shadow-2xl p-8 fade-in" 
          style={{
            background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.15) 0%, rgba(17, 17, 17, 0.95) 50%, rgba(139, 92, 246, 0.1) 100%)',
            border: '2px solid rgba(139, 92, 246, 0.4)',
            boxShadow: '0 0 40px rgba(139, 92, 246, 0.2), inset 0 0 60px rgba(139, 92, 246, 0.05)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold" style={{
              background: 'linear-gradient(135deg, #8B5CF6, #A78BFA, #C4B5FD)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>Reset Password</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-3xl transition"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                required
                className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="Enter your registered email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                New Password
              </label>
              <input
                type="password"
                required
                className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confirm New Password
              </label>
              <input
                type="password"
                required
                className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>

            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            {resetSuccess && (
              <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-3 rounded-lg">
                ✅ {resetSuccess}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-white"
              style={{
                background: 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 50%, #A78BFA 100%)',
                boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)'
              }}
              onMouseOver={(e) => e.target.style.boxShadow = '0 0 30px rgba(139, 92, 246, 0.6)'}
              onMouseOut={(e) => e.target.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.4)'}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          <p className="text-center text-gray-400 text-sm mt-6">
            Remember your password?{' '}
            <button 
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
              }}
              className="font-medium hover:underline"
              style={{ color: '#A78BFA' }}
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Login Form
  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div 
        className="max-w-md w-full rounded-2xl shadow-2xl p-8 fade-in" 
        style={{
          background: 'linear-gradient(135deg, rgba(107, 70, 193, 0.15) 0%, rgba(17, 17, 17, 0.95) 50%, rgba(139, 92, 246, 0.1) 100%)',
          border: '2px solid rgba(139, 92, 246, 0.4)',
          boxShadow: '0 0 40px rgba(139, 92, 246, 0.2), inset 0 0 60px rgba(139, 92, 246, 0.05)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold" style={{
            background: 'linear-gradient(135deg, #8B5CF6, #A78BFA, #C4B5FD)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>Welcome Back</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-3xl transition"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Email Address
            </label>
            <input
              type="email"
              data-testid="login-email-input"
              required
              className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Password
            </label>
            <input
              type="password"
              data-testid="login-password-input"
              required
              className="w-full px-4 py-3 bg-gray-900/80 border border-purple-500/30 text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition placeholder-gray-500"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
          </div>

          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg" data-testid="login-error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full py-3 rounded-lg font-semibold transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-white"
            style={{
              background: 'linear-gradient(135deg, #6B46C1 0%, #8B5CF6 50%, #A78BFA 100%)',
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)'
            }}
            onMouseOver={(e) => e.target.style.boxShadow = '0 0 30px rgba(139, 92, 246, 0.6)'}
            onMouseOut={(e) => e.target.style.boxShadow = '0 0 20px rgba(139, 92, 246, 0.4)'}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Logging in...
              </span>
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="flex justify-between items-center mt-4">
          <button 
            onClick={() => {
              setShowForgotPassword(true);
              setError('');
            }}
            className="text-sm font-medium hover:underline"
            style={{ color: '#A78BFA' }}
          >
            Forgot Password?
          </button>
        </div>

        <p className="text-center text-gray-400 text-sm mt-4">
          Don't have an account?{' '}
          <button 
            onClick={onShowSignup}
            className="font-medium hover:underline"
            style={{ color: '#A78BFA' }}
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
}

export default LoginModal;
